import { getGroup, type GroupId } from '@lafayette/content';
import type { CSSProperties, ReactNode } from 'react';

import { Card, CARD_WIDTHS, type CardSize } from './Card.js';
import { GroupChip } from './GroupChip.js';
import styles from './MallStack.module.css';

export interface MallStackProps {
  group: GroupId;
  /** Card instance ids in the mall, plus the definition each one maps to. */
  cards: string[];
  upgrades: string[];
  tenantNames: Record<string, string>;
  definitions: Record<string, string>;
  size?: CardSize;
  complete: boolean;
  ownerName: string;
  onSelectMall?: () => void;
  onSelectCard?: (instanceId: string) => void;
  selectableCardIds?: string[];
  /** Cards the action in progress would accept, highlighted for the player. */
  targetableCardIds?: string[];
  /** Cards already chosen, e.g. staged into a payment. */
  selectedCardIds?: string[];
  /** Cards another player has requested from this mall. */
  requestedCardIds?: string[];
  /** Lets the attached upgrades be picked too, which paying a debt needs. */
  onSelectUpgrade?: (instanceId: string) => void;
}

/**
 * One strip mall, fanned the way the cards sit on a real table: each card is
 * offset by exactly its title band, so every tenant name stays readable while
 * only the card on top shows the group's rent schedule in full.
 */
export function MallStack({
  group,
  cards,
  upgrades,
  tenantNames,
  definitions,
  size = 'xs',
  complete,
  ownerName,
  onSelectMall,
  onSelectCard,
  selectableCardIds = [],
  targetableCardIds = [],
  selectedCardIds = [],
  requestedCardIds = [],
  onSelectUpgrade,
}: MallStackProps): ReactNode {
  const meta = getGroup(group);
  const width = CARD_WIDTHS[size];

  const stackStyle: CSSProperties = {
    ['--card-w' as string]: width,
    ['--stack-count' as string]: String(Math.max(cards.length, 1)),
  };

  const body = (
    <>
      <div className={styles.header}>
        <GroupChip groups={group} size="large" />
        <span className={styles.count}>
          {cards.length}/{meta.size}
        </span>
        {complete ? <span className={styles.completeFlag}>Complete</span> : null}
      </div>
      <div className={styles.stack} style={stackStyle}>
        {cards.map((instanceId, index) => {
          const definitionId = definitions[instanceId];
          if (!definitionId) return null;
          const selectable = selectableCardIds.includes(instanceId);
          return (
            <div
              key={instanceId}
              className={styles.stackItem}
              style={{ ['--stack-index' as string]: String(index), zIndex: index + 1 }}
            >
              <Card
                definitionId={definitionId}
                size={size}
                group={group}
                tenantName={tenantNames[instanceId]}
                selected={selectedCardIds.includes(instanceId)}
                targetable={targetableCardIds.includes(instanceId)}
                requested={requestedCardIds.includes(instanceId)}
                context={`${ownerName} ${meta.label} mall, card ${index + 1} of ${cards.length}${
                  requestedCardIds.includes(instanceId) ? ', requested by another player' : ''
                }`}
                onClick={selectable && onSelectCard ? () => onSelectCard(instanceId) : undefined}
              />
            </div>
          );
        })}
      </div>
      {upgrades.length > 0 ? (
        <div className={styles.upgrades}>
          {upgrades.map((instanceId) => {
            const definitionId = definitions[instanceId];
            if (!definitionId) return null;
            const residential = definitionId === 'residential-development-on-site';
            const label = residential ? '+$4M Residential' : '+$3M Grocery';
            const classes = [
              styles.upgrade,
              residential ? styles.upgradeResidential : '',
              onSelectUpgrade ? styles.upgradeButton : '',
              targetableCardIds.includes(instanceId) ? styles.upgradeTargetable : '',
              selectedCardIds.includes(instanceId) ? styles.upgradeSelected : '',
              requestedCardIds.includes(instanceId) ? styles.upgradeRequested : '',
            ]
              .filter(Boolean)
              .join(' ');

            return onSelectUpgrade ? (
              <button
                key={instanceId}
                type="button"
                className={classes}
                data-card={definitionId}
                aria-pressed={selectedCardIds.includes(instanceId)}
                onClick={() => onSelectUpgrade(instanceId)}
              >
                {label}
              </button>
            ) : (
              <span key={instanceId} className={classes}>
                {label}
              </span>
            );
          })}
        </div>
      ) : null}
    </>
  );

  const classes = `${styles.mall} ${complete ? styles.complete : ''}`;

  if (!onSelectMall) {
    return (
      <div className={classes} data-mall={group}>
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`${classes} ${styles.selectable}`}
      onClick={onSelectMall}
      data-mall={group}
      aria-label={`${ownerName} ${meta.label} mall, ${cards.length} of ${meta.size} leased${
        complete ? ', complete' : ''
      }`}
    >
      {body}
    </button>
  );
}
